import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { Auth } from '@freelancer/auth';
import * as Sentry from '@sentry/browser';
import { map } from 'rxjs/operators';
import * as UAParser from 'ua-parser-js';
import { DISABLE_ERROR_TRACKING, TRACKING_CONFIG } from './tracking.config';
import { TrackingConfig } from './tracking.interface';

export interface ExtraData {
  [k: string]: any;
}

@Injectable()
export class ErrorTracking {
  isSentryEnabled: boolean;

  constructor(
    private auth: Auth,
    @Inject(TRACKING_CONFIG) private config: TrackingConfig,
    @Inject(PLATFORM_ID) platformId: Object,
    @Inject(DISABLE_ERROR_TRACKING) @Optional() disableErrorTracking: boolean,
  ) {
    if (
      isPlatformBrowser(platformId) &&
      this.config.enableErrorTracking &&
      this.isCurrentBrowser() &&
      !disableErrorTracking
    ) {
      Sentry.init({
        dsn: this.config.sentryDsn,
        release: this.config.gitRevision,
        environment: this.config.sentryEnvironment,
        ignoreErrors: [
          // This is caused by anchorScrolling, see
          // https://github.com/angular/angular/issues/26854.
          // It's harmless though.
          /SyntaxError: Failed to execute 'querySelector' on 'Document': '.*' is not a valid selector/,
          /SyntaxError: '.*' is not a valid selector/,
          /SyntaxError: SyntaxError/,
          /SyntaxError: The string did not match the expected pattern/,
          // These are caused by 1/ Ad blockers blocking certain requests, e.g.
          // twitter or facebook SVGs, and 2/ browsers cancelling ongoing
          // requests due to a location change
          'Http failure response for (unknown url): 0',
          /Error: Loading chunk \d+ failed/,
          // Random client errors we can't do much about but show a banner at
          // some point
          'Out of memory',
        ],
        blacklistUrls: [/^file:\/\//, /^\/ext/],
        integrations: integrations =>
          // integrations will be all default integrations
          [
            ...integrations.filter(
              integration =>
                !['TryCatch', 'GlobalHandlers'].includes(integration.name),
            ),
            new Sentry.Integrations.ReportingObserver(),
          ],
        beforeSend(event) {
          if (event.message && event.message.startsWith('ReportingObserver')) {
            // There's a Boomerang bug where getEntriesByType('navigation')
            // (Nav Timings 2) is called before it's ready, leading Boomerang
            // to fallback to chrome.loadTimes()
            if (event.message.includes('chrome.loadTimes() is deprecated')) {
              return null;
            }
            // This is caused by perfect-scrolling, remove when/if
            // https://github.com/utatti/perfect-scrollbar/pull/810 ever get
            // merged.
            if (
              event.message.includes(
                'Ignored attempt to cancel a touchmove event',
              )
            ) {
              return null;
            }
            // Only include events generated from the webapp source files in
            // order to cut off the noise
            if (
              event.extra &&
              event.extra.body &&
              event.extra.body.sourceFile &&
              event.extra.body.sourceFile.includes('/assets/webapp/')
            ) {
              return event;
            }
            return null;
          }
          return event;
        },
      });
      Sentry.configureScope(scope => {
        scope.setTag(
          'releaseDate',
          new Date(this.config.buildTimestamp).toUTCString(),
        );
      });
      this.isSentryEnabled = true;
    }
  }

  captureMessage(msg: string, extras?: ExtraData): void {
    if (this.isSentryEnabled && this.isCurrentBrowser()) {
      this.auth.authState$
        .pipe(
          map(auth => {
            Sentry.withScope(scope => {
              if (auth) {
                scope.setUser({ id: auth.userId });
              }
              if (extras) {
                Object.entries(extras).forEach(([key, value]) => {
                  scope.setExtra(key, value);
                });
              }
              Sentry.captureMessage(msg);
            });
          }),
        )
        .toPromise();
    }
  }

  captureException(error: Error, extras?: ExtraData): void {
    if (this.isSentryEnabled && this.isCurrentBrowser()) {
      this.auth.authState$
        .pipe(
          map(auth => {
            Sentry.withScope(scope => {
              if (auth) {
                scope.setUser({ id: auth.userId });
              }
              if (extras) {
                Object.entries(extras).forEach(([key, value]) => {
                  scope.setExtra(key, value);
                });
              }
              Sentry.captureException(error);
            });
          }),
        )
        .toPromise();
    }
  }

  // yeah UA parsing is bad blah blah but catching large failures quickly is
  // more important than being flooded by exotic browsers.
  private isCurrentBrowser() {
    const ua = new UAParser(window.navigator.userAgent);
    const { name, version = '' } = ua.getBrowser();
    if (
      (name === 'Edge' && parseInt(version, 10) >= 17) ||
      (name === 'Firefox' &&
        parseInt(version, 10) >= 65 &&
        !(ua.getOS().name === 'Android')) ||
      (name === 'Chrome' && parseInt(version, 10) >= 72) ||
      (name === 'Safari' && parseInt(version, 10) >= 11)
    ) {
      return true;
    }
    return false;
  }
}
